import {
  CompanyCatalogApi,
  EvaluationsApi,
  FilterEnums,
  HabilitationsApi,
  ItemType,
  RequestsApi,
  ItemType as SettingItemType,
  SettingScopesEnum,
  TrainingPlanApi,
  UserDetailApi,
  UsersApi,
} from "@simbelapp/order-sdk";

import { useCookie, useState } from "#imports";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { usePagination } from "~/composables/paginations/usePagination";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { DATE_FILTER_IDS } from "~/utils/enums/filters.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import { useCompanyTagsStore } from "~/store/companies/tags.store";
import { useSettingsStore } from "~/store/settings/settings.store";
import { useManagersStore } from "~/store/managers/managers.store";
import {
  filterValueKey,
  normalizeFilterOption,
  optionsAreEntities,
  type IFilterSectionResponse,
} from "~/utils/helpers/filterValues";

export interface IFilterOption {
  filterId: string;
  default: boolean;
  available: boolean;
  active: boolean;
  values?: Array<any>;
  selectedValues: Array<any>;
  filteredValues?: Array<any>;
  filterName?: string;
  loading?: boolean;
}
interface IFilterCategory {
  training: Set<IFilterOption>;
  employee: Set<IFilterOption>;
  requests: Set<IFilterOption>;
  habilitation: Set<IFilterOption>;
  companyCatalog: Set<IFilterOption>;
  catalog: Set<IFilterOption>;
  enquete: Set<IFilterOption>;
}

interface IFilterCategory {
  training: Set<IFilterOption>;
  employee: Set<IFilterOption>;
  requests: Set<IFilterOption>;
  habilitation: Set<IFilterOption>;
  catalog: Set<IFilterOption>;
}

enum FilterSectionsEnum {
  TRAINING = "training",
  EMPLOYEE = "employee",
  REQUESTS = "requests",
  HABILITATION = "habilitation",
  COMPANY_CATALOG = "companyCatalog",
  CATALOG = "catalog",
  REMAINING_TASKS = "remaining_tasks",
  ENQUETE = "session_form", // backend section_id for the survey filter family (displayed as "Enquête")
}

export type FetchType =
  | "individual"
  | "sessions"
  | "requests"
  | "users"
  | "habilitations"
  | "companyCatalog"
  | "employeeCatalog"
  | "training"
  // Cross-session Évaluations page — reads the section-shaped `/evaluations/filters`
  // (enquête + training + employee families) through the standard filters machinery.
  | "evaluations";

type ExtendedItemType = {
  sessions: typeof ItemType.Session;
  individual: typeof ItemType.Session;
  training: typeof ItemType.Training;
  employeeCatalog?: typeof ItemType.Training;
  companyCatalog?: any;
  habilitations?: any;
  requests?: any;
  users?: any;
  evaluations?: any; // no trainingTags filter for evaluations — key kept for FetchType parity
};

/**
 * One filters endpoint pair — `…/filters` and `…/filters/{filter_id}` — behind the typed SDK.
 * Values stay `any[]`: that route is heterogeneous by nature (`string[] | {id,name}[] | null`).
 */
interface IFiltersClient {
  fetchSections: () => Promise<IFilterSectionResponse[]>;
  fetchValues: (filterId: string) => Promise<any[]>;
}

// The per-user filter bars reuse this machinery through `endpointOverride`, and the user id is the
// only part of their path that varies — so it is read back from the endpoint the caller resolved.
const USER_DETAIL_REQUESTS_ENDPOINT = /^\/userDetail\/user-requests\/([^/]+)$/;
const USER_DETAIL_TRAININGS_ENDPOINT = /^\/userDetail\/user-trainings\/([^/]+)$/;

export interface IExcludedValues {
  [filterId: string]: (string | number | null | any)[];
}

/** How long a page that keeps its filters in a cookie (/catalog) remembers them. */
const FILTERS_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

export const useFilters = (context: string = "default", excludedValues?: IExcludedValues) => {
  const showFiltersOptions = useState<boolean>(`filters_show_${context}`, () => false);
  const filtersOptions = useState<IFilterCategory>(`filters_categories_${context}`, () => ({
    training: new Set(),
    employee: new Set(),
    requests: new Set(),
    habilitation: new Set(),
    companyCatalog: new Set(),
    catalog: new Set(),
    enquete: new Set(),
  }));
  const activeFilters = useState<Set<IFilterOption>>(`filters_active_${context}`, () => new Set());
  const endpoint = useState<string>(`endpoint_type_${context}`, () => "");
  const filtersType = useState<FetchType>(`filters_type_${context}`, () => "requests");
  const microservice = useState<MicroserviceEnum.ORDERS>(`microservice_${context}`, () => MicroserviceEnum.ORDERS);
  // The cookie this context keeps its filters in. Only a page that names one through
  // `setMicroservice()` has it (/catalog); every other context upserts its grid settings or keeps its
  // filters in memory, so it reads and writes no cookie at all.
  const activeCookie = useState<string | null>(`filters_opts_${context}`, () => null);
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);
  const tagsStore = useCompanyTagsStore();
  const route = useRoute();
  const settingsStore = useSettingsStore();
  const managersStore = useManagersStore();
  const { gridDetails } = storeToRefs(settingsStore);

  const endpoints: Record<FetchType, string> = {
    sessions: "/TrainingPlan/session-view",
    individual: "/TrainingPlan/individual-view",
    requests: "/requests",
    users: "/users",
    habilitations: "/habilitations",
    companyCatalog: "/companyCatalog/company-catalog",
    employeeCatalog: "/companyCatalog/employee-catalog",
    training: "/companyCatalog/simbel-catalog",
    evaluations: "/session-forms/overview", // filters served at /session-forms/overview/filters (+ /filters/:id for values)
  };

  /**
   * The typed SDK pair serving the currently resolved endpoint. `endpoint.value` is the routing key
   * — it already is what `fetchFilters` resolved, override included — so a per-user bar is matched
   * first and everything else dispatches on the filter family.
   */
  async function getFiltersClient(): Promise<IFiltersClient> {
    const ms = microservice.value ?? MicroserviceEnum.ORDERS;

    const requestsUserId = endpoint.value.match(USER_DETAIL_REQUESTS_ENDPOINT)?.[1];
    if (requestsUserId) {
      const api = await useSDKApi(ms, UserDetailApi);
      return {
        fetchSections: () => api.getUserDetailRequestFilters({ userId: requestsUserId }),
        fetchValues: (filterId) => api.getUserDetailRequestFilterValues({ userId: requestsUserId, filterId }),
      };
    }

    const trainingsUserId = endpoint.value.match(USER_DETAIL_TRAININGS_ENDPOINT)?.[1];
    if (trainingsUserId) {
      const api = await useSDKApi(ms, UserDetailApi);
      return {
        fetchSections: () => api.getUserDetailTrainingFilters({ userId: trainingsUserId }),
        fetchValues: (filterId) => api.getUserDetailTrainingFilterValues({ userId: trainingsUserId, filterId }),
      };
    }

    switch (filtersType.value) {
      case "users": {
        const api = await useSDKApi(ms, UsersApi);
        return {
          fetchSections: () => api.getUsersFilters(),
          fetchValues: (filterId) => api.getUsersFilterValues({ filterId }),
        };
      }
      case "requests": {
        const api = await useSDKApi(ms, RequestsApi);
        return {
          fetchSections: () => api.getRequestsFilters(),
          fetchValues: (filterId) => api.getRequestsFilterValues({ filterId }),
        };
      }
      case "habilitations": {
        const api = await useSDKApi(ms, HabilitationsApi);
        return {
          fetchSections: () => api.getHabilitationsFilters(),
          // Historic name on the habilitations controller, kept by the SDK.
          fetchValues: (filterId) => api.findFilterValues({ filterId }),
        };
      }
      case "companyCatalog": {
        const api = await useSDKApi(ms, CompanyCatalogApi);
        return {
          fetchSections: () => api.getCompanyCatalogFilters(),
          fetchValues: (filterId) => api.getCompanyCatalogFilterValues({ filterId }),
        };
      }
      case "employeeCatalog": {
        const api = await useSDKApi(ms, CompanyCatalogApi);
        return {
          fetchSections: () => api.getEmployeeCatalogFilters(),
          fetchValues: (filterId) => api.getEmployeeCatalogFilterValues({ filterId }),
        };
      }
      case "training": {
        const api = await useSDKApi(ms, CompanyCatalogApi);
        return {
          fetchSections: () => api.getSimbelCatalogFilters(),
          fetchValues: (filterId) => api.getSimbelCatalogFilterValues({ filterId }),
        };
      }
      case "individual": {
        const api = await useSDKApi(ms, TrainingPlanApi);
        return {
          fetchSections: () => api.getIndividualViewFilters(),
          fetchValues: (filterId) => api.getIndividualViewFilterValues({ filterId }),
        };
      }
      case "sessions": {
        const api = await useSDKApi(ms, TrainingPlanApi);
        return {
          fetchSections: () => api.getSessionViewFilters(),
          fetchValues: (filterId) => api.getSessionViewFilterValues({ filterId }),
        };
      }
      case "evaluations": {
        const api = await useSDKApi(ms, EvaluationsApi);
        return {
          fetchSections: () => api.getEvaluationFilters(),
          fetchValues: (filterId) => api.getEvaluationFilterValues({ filterId }),
        };
      }
    }
  }

  function reset() {
    filtersOptions.value.employee.clear();
    filtersOptions.value.training.clear();
    filtersOptions.value.companyCatalog.clear();
    filtersOptions.value.requests.clear();
    filtersOptions.value.habilitation.clear();
    filtersOptions.value.enquete.clear();

    activeFilters.value.clear();
    showFiltersOptions.value = false;
  }

  async function fetchValues(filterId: string): Promise<any[]> {
    const itemType: ExtendedItemType = {
      sessions: ItemType.Session,
      individual: ItemType.Session,
      training: ItemType.Training,
    };

    if (["training_tags"].includes(filterId)) {
      if (user.value?.mainEntityId) {
        const data = await tagsStore.getTrainingTags({
          companyId: user.value.mainEntityId.companyId,
          onlyUsed: "true",
          itemType: itemType[filtersType.value],
          onlyCatalogFilterVisible: route.path === "/catalog" ? "true" : undefined,
        });
        if (data) {
          const transformedData = data.map((tag) => {
            return {
              ...tag,
              id: tag?.tagId,
            };
          });
          return transformedData;
        }
      }
    } else {
      try {
        const client = await getFiltersClient();
        return (await client.fetchValues(filterId)) ?? [];
      } catch {
        // One filter's values failing must not toast once per filter in the bar, nor stop the others
        // from loading: the filter simply offers no option, as it did when this was a raw fetch.
        return [];
      }
    }

    return [];
  }

  function setMicroservice(ms: MicroserviceEnum.ORDERS, cookie: string | null = null) {
    reset();
    microservice.value = ms;
    activeCookie.value = cookie;
  }

  function filtersCookie(name: string) {
    return useCookie<Record<string, string> | null>(name, { maxAge: FILTERS_COOKIE_MAX_AGE });
  }

  async function getCookieFilter(filterId: string) {
    if (!activeCookie.value) return null;

    const values = filtersCookie(activeCookie.value).value?.[filterId];
    return values ? values.split(",").map((val) => decodeURIComponent(val)) : null;
  }

  function clearCookieFilters(filterIds: string[]) {
    if (!activeCookie.value) return;

    const cookie = filtersCookie(activeCookie.value);
    if (!cookie.value) return;

    cookie.value = Object.fromEntries(Object.entries(cookie.value).filter(([id]) => !filterIds.includes(id)));
  }
  function getFilter(filterId: string) {
    const existingFilter = gridDetails.value.filtersValues.find((f) => f.filterId === filterId);
    return existingFilter?.values ?? null;
  }

  async function fetchFilters(
    type: FetchType,
    cookie_save?: boolean,
    useSettings: boolean = true,
    endpointOverride?: string,
  ) {
    filtersType.value = type;
    // endpointOverride lets a caller point the standard filters machinery at a different base path
    // (e.g. per-user `/userDetail/:id/requests`) that still follows the `…/filters` + `…/filters/:id` convention.
    endpoint.value = endpointOverride ?? endpoints[type];

    let sections: IFilterSectionResponse[];
    try {
      const client = await getFiltersClient();
      sections = await client.fetchSections();
    } catch {
      // The bar stays empty rather than rejecting: the caller still emits `fetchData`, so the grid
      // it decorates loads unfiltered instead of never rendering at all.
      const feedback = useFeedback();
      feedback.error("Une erreur est survenue", "small");
      return;
    }

    if (sections) {
      reset();

      const findSection = (sectionId: FilterSectionsEnum) =>
        sections.find((section) => section.sectionId === sectionId);

      // form_type ("Type d'enquête", delayed/direct) is redundant with the à chaud / à froid tabs, so it's
      // the only évaluations filter kept out of the bar. manager_direct IS shown: FilterActiveOptions renders
      // it via the lazy RadioListFilter (managers from GET /users/managers), not the /filters/:id checkbox
      // path — which is why it stays in `excludedFilters` below (no empty /evaluations/filters/:id fetch).
      const hiddenForEvaluations = type === "evaluations" ? ["form_type"] : [];
      const isHidden = (f: IFilterOption) => hiddenForEvaluations.includes(f.filterId);

      findSection(FilterSectionsEnum.ENQUETE)
        ?.filters?.map(normalizeFilterOption)
        .filter((f) => !isHidden(f))
        .forEach((f) => filtersOptions.value.enquete.add(f));
      findSection(FilterSectionsEnum.TRAINING)?.filters?.forEach((f) =>
        filtersOptions.value.training.add(normalizeFilterOption(f)),
      );
      findSection(FilterSectionsEnum.EMPLOYEE)
        ?.filters?.map(normalizeFilterOption)
        .filter((f) => !isHidden(f))
        .forEach((f) => filtersOptions.value.employee.add(f));
      findSection(FilterSectionsEnum.REQUESTS)?.filters?.forEach((f) =>
        filtersOptions.value.requests.add(normalizeFilterOption(f)),
      );
      findSection(FilterSectionsEnum.HABILITATION)?.filters?.forEach((f) =>
        filtersOptions.value.habilitation.add(normalizeFilterOption(f)),
      );
      findSection(FilterSectionsEnum.COMPANY_CATALOG)?.filters?.forEach((f) =>
        filtersOptions.value.companyCatalog.add(normalizeFilterOption(f)),
      );
      findSection(FilterSectionsEnum.CATALOG)?.filters?.forEach((f) =>
        filtersOptions.value.companyCatalog.add(normalizeFilterOption(f)),
      );
      findSection(FilterSectionsEnum.REMAINING_TASKS)?.filters?.forEach((f) =>
        filtersOptions.value.companyCatalog.add(normalizeFilterOption(f)),
      );

      for (const opts of Object.values(filtersOptions.value)) {
        for (const val of opts) {
          let values: string[] | null = null;
          if (cookie_save) {
            values = (await getCookieFilter(val.filterId)) ?? null;
          } else if (useSettings) {
            // Only get values from global settings when useSettings is true
            values = (await getFilter(val.filterId)) ?? null;
          }
          if (val.default || values) {
            // Pass the resolved endpoint so a late response from a previous fetch can't populate
            // filters for a different endpoint (the override case still bypasses the endpoints[type] guard).
            await addFilter(val, values, type, endpoint.value);
          }
        }
      }
    }
  }

  async function addFilter(
    filter: IFilterOption,
    default_values?: string[] | null,
    type?: FetchType,
    expectedEndpoint?: string,
  ) {
    /**
     * this filters list excluded element who don't need to fetch datas per example date or price
     * contract_start_date is a datepicker, it's only range and without limit of data.
     */
    const excludedFilters = [...DATE_FILTER_IDS, FilterEnums.ManagerDirect];

    // Drop the result if the endpoint changed while awaiting (stale request from a previous fetch).
    if (expectedEndpoint && expectedEndpoint !== endpoint.value) return;
    if (type && !expectedEndpoint && endpoints[type] !== endpoint.value) return;
    if (filter.available) {
      if (!filter.values?.length && !excludedFilters.includes(filter.filterId)) {
        filter.values = await fetchValues(filter.filterId);
      }

      // Appliquer les exclusions de valeurs si définies pour ce filtre
      if (filter.values && excludedValues && excludedValues[filter.filterId]) {
        const excludedIds = excludedValues[filter.filterId];
        filter.values = filter.values.filter((value) => {
          if (value === null || value === undefined) {
            return !excludedIds.includes("null") && !excludedIds.includes(null);
          }

          // Pour les objets avec un id
          if (typeof value === "object" && value.id !== undefined) {
            return !excludedIds.includes(value.id);
          }

          // Pour les valeurs simples
          return !excludedIds.includes(value);
        });
      }

      filter.selectedValues = [];

      if (default_values) {
        if (filter.filterId === FilterEnums.ManagerDirect) {
          // Lazy filter: settings persist only ids → resolve them to { id, displayValue }
          // via the managers endpoint's user_ids short-circuit (fallback = the raw id).
          const resolved = await managersStore.resolveManagers(default_values);
          filter.selectedValues = default_values.map(
            (id) => resolved.find((r) => r.id === id) ?? { id, displayValue: id },
          );
        } else if (optionsAreEntities(filter.values)) {
          if (filter.values) {
            // Match top-level options by their wire key (entity → id, plain value → itself)
            filter.selectedValues = filter.values.filter((x) => default_values.includes(filterValueKey(x)));
            // Also match nested rooms inside locations
            if (filter.filterId === "location") {
              const matchedRooms = filter.values
                .flatMap((location: any) => location?.rooms || [])
                .filter((room: any) => default_values.includes(room?.id));
              filter.selectedValues.push(...matchedRooms);
            }
          }
        } else {
          filter.selectedValues = default_values.map((x) => (x === "null" ? null : x));
        }
      }

      activeFilters.value.add(filter);
    }
  }

  async function clearFilters(cookie?: boolean, paginationKey?: string) {
    if (cookie) {
      clearCookieFilters([...activeFilters.value].map((filter) => filter.filterId));
    } else {
      gridDetails.value.filtersValues = [];
      if (authStore.user?.userId) {
        await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
      }
    }

    activeFilters.value.clear();
    updateFilters(paginationKey);
  }

  function filtersResetOffset(cookie_save?: boolean, paginationKey?: string) {
    const paginate = usePagination(paginationKey || context);
    paginate.paginateOptions.value.offset = 0;
    if (cookie_save) return;

    gridDetails.value.paginationValues = [];
    if (authStore.user?.userId) {
      settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
    }
  }

  async function setCookieFilterOpt(filter: IFilterOption, paginationKey?: string) {
    if (activeCookie.value) {
      const cookie = filtersCookie(activeCookie.value);
      // Persist each selected value by its wire key — object-valued options ({ id, … }) by their id,
      // plain values by themselves. Shape-driven, like the query string, so a filter served as objects
      // on one endpoint and as strings on another (e.g. `categories`) round-trips correctly either way.
      cookie.value = {
        ...cookie.value,
        [filter.filterId]: filter.selectedValues.map((x) => encodeURIComponent(filterValueKey(x))).join(","),
      };
    }
    filtersResetOffset(true, paginationKey);
    updateFilters(paginationKey);
  }

  async function setFilterOpt({
    filter,
    scope,
    paginationKey,
    upsertSettings,
  }: {
    filter: IFilterOption;
    scope?: SettingScopesEnum;
    paginationKey?: string;
    upsertSettings: boolean;
  }) {
    if (!scope || !upsertSettings) {
      setCookieFilterOpt(filter, paginationKey);
      return;
    }

    const filterExists = gridDetails.value.filtersValues.some(
      (existingFilter) => existingFilter.filterId === filter.filterId,
    );
    if (!filterExists) {
      const newFilter = {
        filterId: filter.filterId as FilterEnums,
        values: filter.selectedValues.map((value) => (value?.id as string) ?? value ?? null),
      };

      gridDetails.value.filtersValues.push(newFilter);
    } else {
      const existingFilter = gridDetails.value.filtersValues.find((f) => f.filterId === filter.filterId);

      if (existingFilter) {
        existingFilter.values = filter.selectedValues.map((value) => (value?.id as string) ?? value ?? null);
      }
    }
    if (authStore.user?.userId && scope) {
      await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
    }
    filtersResetOffset(false, paginationKey);

    updateFilters(paginationKey);
  }

  async function clearCookieFilterOpts(activeFilter: IFilterOption, skipUpdate?: boolean, paginationKey?: string) {
    if (activeFilter.selectedValues.length) {
      clearCookieFilters([activeFilter.filterId]);

      activeFilter.selectedValues = [];
      filtersResetOffset(true, paginationKey);
      if (!skipUpdate) updateFilters(paginationKey);
      // updateKpi();
    }
  }

  async function clearFilterOpts({
    activeFilter,
    skipUpdate,
    cookie,
    paginationKey,
  }: {
    activeFilter: IFilterOption;
    skipUpdate?: boolean;
    cookie?: boolean;
    paginationKey?: string;
  }) {
    if (cookie) {
      await clearCookieFilterOpts(activeFilter, skipUpdate, paginationKey);
    } else if (activeFilter.selectedValues.length) {
      gridDetails.value.filtersValues = gridDetails.value.filtersValues.filter(
        (filter) => filter.filterId !== activeFilter.filterId,
      );
      if (authStore.user?.userId) {
        await settingsStore.updateGridSettings(authStore.user?.userId, SettingItemType.User);
      }
      activeFilter.selectedValues = [];
      filtersResetOffset(false, paginationKey);
      if (!skipUpdate) updateFilters(paginationKey);
      // updateKpi();
    }
  }

  function updateFilters(paginationKey?: string) {
    const paginate = usePagination(paginationKey);
    paginate.fetchData();
  }

  function getFiltersPayload() {
    let queryString = "";
    activeFilters.value.forEach((val) => {
      queryString += queryString.length ? "&" : "";
      // Emit the array form `filters=<id>=<vals>` (repeated), NOT the bracket form `filters[<id>]=<vals>`.
      // The Orders API (NestJS 11 / Express 5) defaults its query parser to "simple", which no longer
      // parses bracket notation into a nested object — it would arrive as a literal, non-whitelisted key
      // and 400. The array form is what FindOptionsDto's @Transform expects (same shape as getFiltersForSDK).
      queryString += `filters=${val.filterId}=${val.selectedValues
        .map((x) => encodeURIComponent(filterValueKey(x)))
        .join(",")}`;
    });

    return queryString;
  }

  function getFiltersForSDK() {
    const queryString: string[] = [];
    activeFilters.value.forEach((val) => {
      queryString.push(`${val.filterId}=${val.selectedValues?.map((x) => filterValueKey(x)).join(",")}`);
    });
    return { filters: queryString };
  }

  function filterValues(filter: IFilterOption, searchQuery?: string) {
    if (filter.values) {
      if (searchQuery && searchQuery.trim().length > 0) {
        const normalizedQuery = searchQuery.trim().toLowerCase();
        filter.filteredValues = filter.values.filter((val) => {
          if (!val || (typeof val === "object" && val.id === undefined)) {
            return false;
          } else if (val.name || val.name === "") {
            return val.name.toLowerCase().includes(normalizedQuery);
          } else if (val.display_value) {
            return val.display_value.toLowerCase().includes(normalizedQuery);
          } else {
            return val.toLowerCase().includes(normalizedQuery);
          }
        });
      } else {
        filter.filteredValues = filter.values;
      }
    }
  }

  return {
    showFiltersOptions,
    filtersOptions,
    activeFilters,
    filtersType,
    getFiltersForSDK,
    setMicroservice,
    addFilter,
    reset,
    clearFilters,
    fetchFilters,
    updateFilters,
    clearFilterOpts,
    getFiltersPayload,
    setFilterOpt,
    filterValues,
  };
};
